/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { each, sortBy, chain, extend, pluck, isArray, reduce } from 'underscore'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import Modal from '~/components/Modal'
import ModalConfirmation from '~/components/shared/ModalConfirmation'
import { get, getEntriesForSelector } from '~/services/api/fileplan'
import { useEsignature } from '~/hooks/useEsignature'
import RcmEsignatureModal from '~/components/esignature/RcmEsignatureModal'
import ApplyPackageStep1 from './step1/ApplyPackageStep1'
import ApplyPackageReviewStep from './review/ApplyPackageReviewStep'

interface ApplyPackageModalProps {
  id: number | string
}

interface PackageData {
  id?: number
  name?: string
  filePlanId?: number
  filePlanName?: string
  level?: string
  entries?: any[]
  [key: string]: any
}

interface OptionsData {
  filePlanPromise?: Promise<any>
  artifactsPromise?: Promise<any>
  tagsForPicker?: any[]
  packageCopy?: any
  filePlan?: any
  docTypesMap?: Record<string | number, any>
  docTypesForPicker?: any[]
  levels?: any
  [key: string]: any
}

interface ConfigData {
  levels?: any
  tags?: {
    actions?: {
      new?: boolean
      associate?: boolean
    }
    tagName?: string
    tags?: any[]
  }
  [key: string]: any
}

const ApplyPackageModal: React.FC<ApplyPackageModalProps> = ({ id }) => {
  const context = useServiceContext()
  const { t } = useTranslation()
  const { service, api, $rootScope } = context
  const { translate } = context.service.filter
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()
  const [currentStep, setCurrentStep] = useState('STEP1')
  const [packageData, setPackageData] = useState<PackageData | null>(null)
  const [config, setConfig] = useState<ConfigData>({})
  const [options, setOptions] = useState<OptionsData | null>(null)
  const [isOpen, setIsOpen] = useState(true)
  const [levelValidationSuccess, setLevelValidationSuccess] = useState(false)
  const [contentValidationSuccess, setContentValidationSuccess] = useState(false)
  const [tagsValidationSuccess, setTagsValidationSuccess] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const LEVEL = {
    PRODUCT: 'PRODUCT',
    STUDY: 'STUDY',
    COUNTRY: 'COUNTRY',
    SITE: 'SITE'
  }

  useEffect(() => {
    $rootScope.$on('levelValidation', (evt, data) => {
      setLevelValidationSuccess(data)
    })
    $rootScope.$on('contentValidation', (evt, data) => {
      setContentValidationSuccess(data)
    })
    $rootScope.$on('tagsValidation', (evt, data) => {
      setTagsValidationSuccess(data)
    })
  }, [])

  useEffect(() => {
    Promise.all([api.packages.get(String(id)), api.documentTypes.getAllForPicker(), api.tags.getAllForPicker()]).then(
      (results) => {
        const pckgData = results[0] as PackageData
        let tagsData = results[2] as any[]
        if (pckgData?.entries) {
          each(pckgData.entries, (entry: any) => {
            entry.docTypeIdOrig = entry.docTypeId
          })
        }
        setPackageData(pckgData)
        const docTypesData: any = {
          docTypesForPicker: sortBy(results[1], 'name'),
          docTypesMap: reduce(
            results[1],
            (mem: Record<string | number, any>, docType: any) => {
              mem[docType.id] = docType
              return mem
            },
            {} as Record<string | number, any>
          )
        }
        tagsData = (isArray(tagsData) && tagsData.filter((item: any) => !item?.type || item?.type !== 'SYSTEM')) || []
        init(pckgData, tagsData, docTypesData)
      }
    )
  }, [id])

  const init = (pckgData: PackageData, tagsData: any[], docTypesData: any) => {
    const packageDataLocal = pckgData
    packageDataLocal.entries = packageDataLocal?.entries
      ? chain(packageDataLocal.entries)
          .sortBy('name')
          .map((entry: any) => {
            return extend(entry, {
              alias: entry?.fileName
            })
          })
          .value()
      : []

    const optionsData: OptionsData = {
      filePlanPromise: packageDataLocal?.filePlanId ? get(packageDataLocal.filePlanId) : Promise.resolve(null),
      artifactsPromise:
        packageDataLocal?.filePlanId && packageDataLocal?.level
          ? getEntriesForSelector(String(packageDataLocal.filePlanId), packageDataLocal.level)
          : Promise.resolve([]),
      tagsForPicker: tagsData,
      packageCopy: cloneDeep(packageDataLocal)
    }

    extend(optionsData, docTypesData)

    optionsData.artifactsPromise?.catch(function (result) {
      service.toast.error(result && result.data ? result.data.message : 'ERROR')
      doClose()
    })

    if (optionsData.filePlanPromise) {
      optionsData.filePlanPromise.then((resp: any) => {
        optionsData.filePlan = resp
        setOptions((data) => ({ ...data, filePlan: resp }))
      })
    }
    setOptions(optionsData)
    setPackageData(packageDataLocal)
  }

  const save = async () => {
    // Check if e-signature is required (matching legacy ApplyPackageModal.jsx logic)
    if (context.service.auth.requireESignatureForFileCommands()) {
      try {
        // Build e-signature message matching legacy implementation
        const esignMsg = t('ADMIN_ACTION', { action: translate('ACTION_APPLY_PACKAGE') })

        // Show e-signature modal and wait for validation
        const validation = await signAction('ADMIN_ACTION', esignMsg)

        // Extract only the fields needed by the backend API
        let eSignatureValidation = null
        if (validation.relayState) {
          // SSO/OKTA authentication - extract only relayState
          eSignatureValidation = { relayState: validation.relayState }
        } else {
          // DSO authentication - extract only username/password
          eSignatureValidation = {
            username: validation.username || '',
            password: validation.password || ''
          }
        }

        return apply(null, eSignatureValidation)
      } catch (error) {
        // User cancelled e-signature or authentication failed
        console.log('E-signature cancelled by user')
        return
      }
    } else {
      return apply(null, null)
    }
  }

  const apply = (modal: any, validation?: any) => {
    const data = buildRequestData()
    if (validation) {
      extend(data, {
        esignature: validation
      })
    }

    return context.api.packages.applyPackage(data).then(
      () => {
        doClose()
        setShowSuccessModal(true)
        context.service.toast.success('SETTINGS_PACKAGE_APPLY_SUCCESS_MSG')
      },
      (resp: any) => {
        if (resp?.data?.message) {
          context.service.toast.error(resp.data.message)
        } else {
          context.service.toast.error('ERROR')
        }
      }
    )
  }

  const buildRequestData = () => {
    const data: any = {
      id: packageData?.id
    }
    extend(data, buildTagsRequestData(config?.tags))
    extend(data, buildLevelRequestData(config?.levels, options?.levels))
    if (packageData) {
      extend(data, buildContentsRequestData(packageData))
    }
    return data
  }

  const buildLevelRequestData = (confData: any, optsData: any) => {
    return {
      fsiIds: (() => {
        switch (confData?.level) {
          case LEVEL.SITE:
            return pluck(
              confData?.sites?.length === 1 && confData.sites[0]?.fsiId === 'ALL'
                ? optsData.sites.filter((item: any) => item?.fsiId != 'ALL')
                : confData.sites,
              'fsiId'
            )
          case LEVEL.COUNTRY:
            return pluck(
              confData?.countries?.length === 1 && confData.countries[0]?.fsiId === 'ALL'
                ? optsData?.countries.filter((item: any) => item?.fsiId != 'ALL')
                : confData?.countries,
              'fsiId'
            )
          case LEVEL.STUDY:
            return confData?.environment ? [confData.environment?.fsiId] : [confData?.trial?.fsiId]
        }
      })()
    }
  }

  const buildContentsRequestData = (packageDataRef: PackageData) => {
    return {
      contents: packageDataRef?.entries?.map((entry: any) => {
        const requestEntry: any = {
          entryId: entry?.id,
          name: entry?.alias ? entry.alias : entry.fileName,
          metadata: entry.metadata
        }

        if (entry?.docTypeIdOrig !== entry?.docTypeId) {
          requestEntry.docTypeTplId = entry.docTypeId
        }

        return requestEntry
      })
    }
  }

  const buildTagsRequestData = (confData: any) => {
    return extend(
      {},
      confData?.actions?.new
        ? {
            newTagName: confData?.tagName
          }
        : {},
      confData?.actions?.associate
        ? {
            tags: pluck(confData?.tags, 'id')
          }
        : {}
    )
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const getStep = () => {
    switch (currentStep) {
      case 'STEP1':
        return (
          <ApplyPackageStep1
            nextStep={(packageData, optionsData, configData, nextStepItem) =>
              nextStep(packageData, optionsData, configData, nextStepItem)
            }
            doClose={doClose}
            packageData={packageData!}
            config={config}
            options={options!}
            updateConfigData={(data) => setConfig(data)}
            updateOptionsData={(data) => setOptions(data)}
            updatePackageData={(data) => setPackageData(data)}
          />
        )
      case 'REVIEW':
        return (
          <ApplyPackageReviewStep
            packageData={packageData!}
            config={config}
            options={options!}
            nextStep={(packageData, optionsData, configData, nextStepItem) =>
              nextStep(packageData, optionsData, configData, nextStepItem)
            }
            save={save}
            doClose={doClose}
          />
        )
    }
  }

  const nextStep = (pckgData: PackageData, optionsData: OptionsData, configData: ConfigData, nextStepItem: string) => {
    setPackageData(() => pckgData)
    setOptions(() => optionsData)
    setConfig(() => configData)
    if (nextStepItem === 'REVIEW') {
      ;($rootScope as any).$emit('validatePackageForms', packageData, config)
      if (
        levelValidationSuccess &&
        contentValidationSuccess &&
        (!config?.tags?.actions?.associate || tagsValidationSuccess)
      ) {
        setCurrentStep(nextStepItem)
      }
    } else {
      setCurrentStep(nextStepItem)
    }
  }

  return (
    <>
      <Modal
        show={isOpen}
        className="theme-sandman"
        handleClose={doClose}
        title={translate('SETTINGS_PACKAGE_APPLY_TITLE')}
        size="full"
      >
        <div className="apply-packages-wrapper">
          <div id="apply-package-dialog">{getStep()}</div>
        </div>
      </Modal>
      {showSuccessModal && (
        <ModalConfirmation
          titleMsg={translate('SETTINGS_PACKAGE_APPLY_SUCCESS_TITLE')}
          msg={translate('SETTINGS_PACKAGE_APPLY_SUCCESS_MSG')}
          cancelTxt=""
          okTxt={translate('OK') || 'OK'}
          onSubmit={() => setShowSuccessModal(false)}
          show={showSuccessModal}
        />
      )}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default ApplyPackageModal
